import React, { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useFleet, useUpdateFleet } from '../hooks/useFleets'
import { usePushFleet } from '../hooks/useFleetDivergence'
import { FleetConflictError } from '../service/fleetAPI'
import { fleetStrings, getLocale, Locale } from '../i18n/fleetI18n'
import LuminairePicker from './LuminairePicker'
import MinigridPicker from './MinigridPicker'
import FleetProfileSection from './FleetProfileSection'
import ControlPrioritiesPanel from './ControlPrioritiesPanel'
import DivergencePanel from './DivergencePanel'
import minigridAPI from '../../minigrid/service/minigridAPI'
import { useToast } from '../../../common/providers/ToastProvider/useToast'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'
import { PermissionTooltip, useCapability } from '../../rbac'

interface FleetDetailProps {
  fleetId: string
  locale?: Locale
}

const card: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
  background: '#fff',
}

// F2 + F3 — fleet detail: members add/remove, profile editor, divergence UI.
const FleetDetail: React.FC<FleetDetailProps> = ({ fleetId, locale }) => {
  const strings = fleetStrings(locale ?? getLocale())
  const fleetQuery = useFleet(fleetId)
  const updateMutation = useUpdateFleet()
  const pushMutation = usePushFleet()
  // RBAC : membres et liens minigrid = fleet.edit (editor+).
  const canEdit = useCapability('fleet.edit')
  const toast = useToast()

  const fleet = fleetQuery.data

  const handleMembersChange = (nextMembers: string[]) => {
    if (!fleet || !canEdit) return
    updateMutation.mutate(
      {
        fleetId,
        payload: { ...fleet, member_dev_euis: nextMembers },
      },
      {
        onSuccess: () => {
          // FIX E: member change affects which momis get the profile → auto-push.
          // Best-effort: surface partial failures via toast but don't block UI.
          pushMutation.mutate(
            { fleetId },
            {
              onSuccess: (results) => {
                // Aucune cible (flotte sans minigrid lié) : le push n'a atteint
                // aucune lampe → retour d'info explicite plutôt qu'un silence.
                if (results.length === 0) {
                  toast.info(strings.noPushTarget)
                  return
                }
                const failed = results.filter((r) => !r.pushed)
                if (failed.length > 0) {
                  toast.error(`${strings.failed}: ${failed.length}/${results.length}`)
                }
              },
              onError: () => toast.error(strings.genericError),
            },
          )
        },
        onError: (err) => {
          if (err instanceof FleetConflictError) {
            toast.error(strings.conflictError)
            fleetQuery.refetch()
            return
          }
          toast.error(strings.genericError)
        },
      },
    )
  }

  const handleAdd = (devEui: string) => {
    if (!fleet) return
    if (fleet.member_dev_euis.includes(devEui)) return
    handleMembersChange([...fleet.member_dev_euis, devEui])
  }

  const handleRemove = (devEui: string) => {
    if (!fleet) return
    handleMembersChange(fleet.member_dev_euis.filter((e) => e !== devEui))
  }

  // Minigrid link (platform organizational association). Saved via the SAME
  // full-document fleet update, but WITHOUT the auto-push: the minigrid link
  // does not affect device config, so there is no momi to reconfigure.
  const minigridsQuery = useQuery({
    queryKey: ['minigrids', 'all'],
    queryFn: () => minigridAPI.getAllMinigrids(0, 0),
    staleTime: 30_000,
  })
  const minigridNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of minigridsQuery.data?.data ?? []) {
      map.set(m.minigrid_id, m.minigrid_name)
    }
    return map
  }, [minigridsQuery.data])

  const handleMinigridIdsChange = (nextMinigridIds: string[]) => {
    if (!fleet || !canEdit) return
    updateMutation.mutate(
      {
        fleetId,
        // Full fleet object (PUT is a full-document replace). No auto-push.
        payload: { ...fleet, minigrid_ids: nextMinigridIds },
      },
      {
        onError: (err) => {
          if (err instanceof FleetConflictError) {
            toast.error(strings.conflictError)
            fleetQuery.refetch()
            return
          }
          toast.error(strings.genericError)
        },
      },
    )
  }

  const handleAddMinigrid = (minigridId: string) => {
    if (!fleet) return
    const current = fleet.minigrid_ids ?? []
    if (current.includes(minigridId)) return
    handleMinigridIdsChange([...current, minigridId])
  }

  const handleRemoveMinigrid = (minigridId: string) => {
    if (!fleet) return
    handleMinigridIdsChange(
      (fleet.minigrid_ids ?? []).filter((id) => id !== minigridId),
    )
  }

  if (fleetQuery.isLoading) {
    return <div style={{ fontSize: 13, color: '#6b7280' }}>…</div>
  }
  if (!fleet) {
    return <div style={{ fontSize: 13, color: '#6b7280' }}>—</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
        {fleet.fleet_name}
      </h3>

      {/* Members */}
      <div style={card}>
        <h4 style={{ marginTop: 0, fontSize: 15, color: '#111827' }}>
          {strings.members} ({fleet.member_dev_euis.length})
        </h4>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: '1 1 280px', minWidth: 240 }}>
            {fleet.member_dev_euis.length === 0 ? (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {strings.noMembers}
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {fleet.member_dev_euis.map((eui) => (
                  <li
                    key={eui}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: 13,
                    }}
                  >
                    <span>{eui}</span>
                    <PermissionTooltip allowed={canEdit}>
                      <ButtonV2
                        type="button"
                        onClick={() => handleRemove(eui)}
                        disabled={!canEdit || updateMutation.isLoading}
                        variant="outlined"
                        color="secondary"
                        size="small"
                      >
                        {strings.removeFromFleet}
                      </ButtonV2>
                    </PermissionTooltip>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {canEdit && (
            <div style={{ flex: '0 0 auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {strings.addLuminaires}
              </div>
              <LuminairePicker
                currentFleetId={fleetId}
                currentMembers={fleet.member_dev_euis}
                onAdd={handleAdd}
                strings={strings}
              />
            </div>
          )}
        </div>
      </div>

      {/* Minigrids (platform organizational link — no auto-push) */}
      <div style={card}>
        <h4 style={{ marginTop: 0, fontSize: 15, color: '#111827' }}>
          {strings.minigrids} ({(fleet.minigrid_ids ?? []).length})
        </h4>
        {(fleet.minigrid_ids ?? []).length === 0 && (
          <div
            style={{
              border: '1px solid #fcd34d',
              background: '#fffbeb',
              color: '#92400e',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {strings.noMinigridWarning}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: '1 1 280px', minWidth: 240 }}>
            {(fleet.minigrid_ids ?? []).length === 0 ? (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {strings.noMinigrids}
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {(fleet.minigrid_ids ?? []).map((id) => (
                  <li
                    key={id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: 13,
                    }}
                  >
                    <span>{minigridNameById.get(id) ?? id}</span>
                    <PermissionTooltip allowed={canEdit}>
                      <ButtonV2
                        type="button"
                        onClick={() => handleRemoveMinigrid(id)}
                        disabled={!canEdit || updateMutation.isLoading}
                        variant="outlined"
                        color="secondary"
                        size="small"
                      >
                        {strings.removeMinigrid}
                      </ButtonV2>
                    </PermissionTooltip>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {canEdit && (
            <div style={{ flex: '0 0 auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {strings.addMinigrid}
              </div>
              <MinigridPicker
                currentMinigridIds={fleet.minigrid_ids ?? []}
                onAdd={handleAddMinigrid}
                disabled={updateMutation.isLoading}
                strings={strings}
              />
            </div>
          )}
        </div>
      </div>

      {/* Control priorities (read-only reference: UL20xx dimming-source priority) */}
      <div style={card}>
        <ControlPrioritiesPanel strings={strings} />
      </div>

      {/* Profile */}
      <div style={card}>
        <FleetProfileSection fleet={fleet} strings={strings} />
      </div>

      {/* Divergence (F3) */}
      <div style={card}>
        <DivergencePanel fleetId={fleetId} strings={strings} />
      </div>
    </div>
  )
}

export default FleetDetail
